import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCwIcon, PlusIcon } from "lucide-react";
import { usePermissions } from "../../../PermissionsProvider";
import { CompassService, CompassSearch } from "../../../services/CompassService";
import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

const STATUS_VARIANT: Record<string, string> = {
  PENDING: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  RUNNING: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  COMPLETED: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  FAILED: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
  CANCELLED: "bg-muted text-muted-foreground border-border",
};

function formatDate(ts: number | string | null): string {
  if (!ts) return "\u2014";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CompassSearches: React.FC = () => {
  const navigate = useNavigate();
  const { apiKey } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [searches, setSearches] = useState<CompassSearch[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const loadSearches = useCallback(() => {
    if (!apiKey) return;
    const offset = (currentPage - 1) * pageSize;
    CompassService.getSearches(offset, pageSize, setIsLoading, apiKey).subscribe(
      (response: { items: CompassSearch[]; total: number }) => {
        setSearches(response.items);
        setTotal(response.total);
        setIsLoading(false);
      },
      (error: unknown) => {
        console.error("Failed to load compass searches:", error);
        toast.error("Failed to load searches");
        setIsLoading(false);
      }
    );
  }, [apiKey, currentPage]);

  useEffect(() => {
    loadSearches();
  }, [loadSearches]);

  const totalPages = Math.ceil(total / pageSize);

  const sorted = [...searches].sort((a, b) => b.date_created - a.date_created);

  return (
    <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
      <Card className="border-border">
        <CardContent className="p-8">
          <div className="space-y-1">
            <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
              Compass Searches
            </h1>
            <p className="text-lg text-muted-foreground font-gc-reg">
              Bayesian hyperparameter optimization for finetuning
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={loadSearches} disabled={isLoading}>
          <RefreshCwIcon className="w-4 h-4 mr-1" />
          Refresh
        </Button>
        <Button size="sm" onClick={() => navigate("/admin/compass/new")}>
          <PlusIcon className="w-4 h-4 mr-1" />
          New Search
        </Button>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Composition</TableHead>
                <TableHead>Base Model</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Best Accuracy</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && searches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : searches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No searches yet. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((s) => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/admin/compass/${s.id}?company_id=${s.company_id}`)}
                  >
                    <TableCell className="font-mono text-xs">{s.id}</TableCell>
                    <TableCell className="text-sm truncate max-w-[200px]">
                      {s.composition_name || s.composition_id}
                    </TableCell>
                    <TableCell className="font-medium">{s.base_model}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{s.training_method}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_VARIANT[s.status] || ""}>
                        {s.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {s.best_accuracy != null
                        ? `${(s.best_accuracy * 100).toFixed(1)}%`
                        : "\u2014"}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(s.date_created)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {total} search{total !== 1 ? "es" : ""}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground flex items-center px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompassSearches;
